import { HttpService } from "../modules/shared/services/http.service";
import { Withdrawal } from "../types/payment";
import { RoleEnum } from "../modules/shared/model";

interface CreateNotificationPayload {
  status: { id: number };
  priority: { id: number };
  type: { id: number };
  title: string;
  message: string;
  url: string;
  user: { id: string };
}

// Notification Enums
const NotificationStatus = {
  UNREAD: 1,
  READ: 2,
};

const NotificationPriority = {
  LOW: 1,
  MEDIUM: 2,
  HIGH: 3,
  NORMAL: 4,
};

const NotificationType = {
  BANNER: 1,
  POPUP: 2,
  IN_APP: 3,
  EMAIL: 4,
};

export class PayoutNotificationService {
  /**
   * Send withdrawal status update notification to user
   */
  static async sendWithdrawalNotification(
    withdrawal: Withdrawal,
    actionType: "processing" | "completed",
    adminId: string = "admin"
  ): Promise<void> {
    try {
      if (!withdrawal.user?.id) {
        console.warn("Cannot send notification: User ID not found in withdrawal");
        return;
      }

      // Fetch user details to determine their role
      const userDetails = await this.getUserDetails(withdrawal.user.id);
      
      if (!userDetails) {
        console.warn("Cannot send notification: User details not found");
        return;
      }
      
      // Prepare notification message
      const message = actionType === "processing" 
        ? `Your withdrawal of ${this.formatAmount(withdrawal.amount, withdrawal.currency)} is now being processed.`
        : `Your withdrawal of ${this.formatAmount(withdrawal.amount, withdrawal.currency)} has been completed successfully.`;

      const title = actionType === "processing"
        ? "Withdrawal Processing"
        : "Withdrawal Completed";

      // Create notification record
      const notificationPayload: CreateNotificationPayload = {
        status: { id: NotificationStatus.UNREAD },
        priority: { id: NotificationPriority.HIGH },
        type: { id: NotificationType.IN_APP },
        title,
        message,
        url: "/withdrawalhistory",
        user: { id: withdrawal.user.id }
      };

      // Create notification via API
      await HttpService.post(
        "api/v1/notifications",
        notificationPayload,
        { "Content-Type": "application/json" },
        true
      );

      console.log("Withdrawal notification created successfully for user:", withdrawal.user.id);
    } catch (error) {
      console.error("Failed to create withdrawal notification:", error);
      // Don't throw error as this is a notification, not critical to the workflow
    }
  }

  /**
   * Get user details including role information
   */
  private static async getUserDetails(userId: string): Promise<any> {
    try {
      const response = await HttpService.get(`api/v1/users/${userId}`, true, { "x-custom-lang": "en" });
      return response;
    } catch (error) {
      console.error("Failed to fetch user details:", error);
      return null;
    }
  }

  /**
   * Format amount with currency
   */
  private static formatAmount(amount: number | null | undefined, currency: string | null | undefined): string {
    const amt = amount || 0;
    const curr = currency || "USD";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: curr.toUpperCase(),
    }).format(amt);
  }
}

export default PayoutNotificationService;
